import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Settings, User, ChevronRight, Plus, Check } from 'lucide-react';
import OrientationCarousel from '../components/OrientationCarousel';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category → Image Mapping
// BRAND ILLUSTRATIONS: LikeKind scene-style illustrations
// All 10 categories now have brand illustrations
const CATEGORY_ILLUSTRATIONS = {
  'move': '/images/tiles/tile_sports.png',
  'outdoors': '/images/tiles/tile_outdoors_new.png',   // NEW: Hikers on trail
  'create': '/images/tiles/tile_learn.png',
  'play': '/images/tiles/tile_culture.png',
  'listen': '/images/tiles/tile_listen_new.png',       // NEW: People listening together
  'learn': '/images/tiles/tile_learn.png',
  'gather': '/images/tiles/tile_circles.png',
  'care': '/images/tiles/tile_nourish.png',
  'serve': '/images/tiles/tile_serve_new.png',         // NEW: Community service
  'explore': '/images/tiles/tile_explore.png',
};

// Fallback images (Unsplash) - Only used if brand illustration missing
const TILE_IMAGES_FALLBACK = {
  'Move': 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop',
  'Outdoors': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop',
  'Create': 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=400&h=300&fit=crop',
  'Play': 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=400&h=300&fit=crop',
  'Listen': 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=300&fit=crop',
  'Learn': 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=300&fit=crop',
  'Gather': 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300&fit=crop',
  'Care': 'https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=400&h=300&fit=crop',
  'Serve': 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=400&h=300&fit=crop',
  'Explore': 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop',
};

// Circles tile image (canonical LikeKind people in soft circle formation)
const CIRCLES_TILE = '/images/tiles/tile_circles.png';

/**
 * Browse - Opening Screen
 * 
 * MANDATORY BEHAVIOR:
 * - First thing shown: browseable parent categories
 * - No timeline, no examples, no empty placeholders
 * - Categories visible immediately, clickable, scrollable
 * - Orientation cards are OVERLAY only, never replace browse
 */

export default function Browse() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState({});
  const [followedSubcategories, setFollowedSubcategories] = useState([]);
  const [showOrientation, setShowOrientation] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Seed content for browse-for-free mode
  const [seedGroups, setSeedGroups] = useState([]);
  const [seedCommons, setSeedCommons] = useState([]);

  // Fetch main categories and followed subcategories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories/main`);
        if (res.ok) {
          const data = await res.json();
          // API returns array directly, not wrapped in {categories: [...]}
          setCategories(Array.isArray(data) ? data : data.categories || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
    
    // Fetch followed subcategories if user is logged in
    if (user) {
      const fetchFollowed = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/user/followed-subcategories`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setFollowedSubcategories(data.followed_subcategories || []);
          }
        } catch (err) {
          console.error('Error fetching followed subcategories:', err);
        }
      };
      fetchFollowed();
    } else {
      // Fetch seed content for unauthenticated users (browse-for-free mode)
      const fetchSeedContent = async () => {
        try {
          const [groupsRes, commonsRes] = await Promise.all([
            fetch(`${API_URL}/api/browse/seed-groups`),
            fetch(`${API_URL}/api/browse/seed-commons`)
          ]);
          if (groupsRes.ok) {
            const data = await groupsRes.json();
            setSeedGroups(data.groups || []);
          }
          if (commonsRes.ok) {
            const data = await commonsRes.json();
            setSeedCommons(data.commons || []);
          }
        } catch (err) {
          console.error('Error fetching seed content:', err);
        }
      };
      fetchSeedContent();
    }
  }, [user]);

  // Check if orientation should show
  useEffect(() => {
    const hasSeenOrientation = localStorage.getItem('lk_orientation_seen');
    const lastShown = localStorage.getItem('lk_orientation_last');
    const now = Date.now();
    
    if (!hasSeenOrientation || (lastShown && now - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000)) {
      setShowOrientation(true);
    }
  }, []);

  const handleOrientationComplete = () => {
    localStorage.setItem('lk_orientation_seen', 'true');
    localStorage.setItem('lk_orientation_last', Date.now().toString());
    setShowOrientation(false);
  };

  const toggleFollowSubcategory = async (subId, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/auth');
      return;
    }
    
    const isFollowing = followedSubcategories.includes(subId);
    const endpoint = isFollowing ? 'unfollow-subcategory' : 'follow-subcategory';
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/user/${endpoint}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subcategory_id: subId })
      });
      
      if (res.ok) {
        setFollowedSubcategories(prev => 
          isFollowing 
            ? prev.filter(id => id !== subId)
            : [...prev, subId]
        );
        toast.success(isFollowing ? 'Unfollowed' : 'Following');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleCategoryClick = async (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      return;
    }
    
    setSelectedCategory(categoryId);
    
    // Fetch subcategories if not cached
    if (!subcategories[categoryId]) {
      try {
        const res = await fetch(`${API_URL}/api/categories/${categoryId}/children`);
        if (res.ok) {
          const data = await res.json();
          // API returns {subcategories: [...], tags: [...]}
          setSubcategories(prev => ({
            ...prev,
            [categoryId]: data.subcategories || []
          }));
        }
      } catch (err) {
        console.error('Error fetching subcategories:', err);
      }
    }
  };

  const handleSubcategoryClick = (subId, subSlug) => {
    navigate(`/category/${subId}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary, #FAFAFA)' }}>
      {/* Header with persistent Base Camp nav */}
      <header 
        className="sticky top-0 z-40"
        style={{ 
          backgroundColor: 'var(--fy-background-primary, #FAFAFA)',
          borderBottom: '1px solid var(--fy-border-subtle, #E5E5E5)'
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              {/* Base Camp Back Button - Always visible */}
              {user && (
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg mr-2 font-semibold shadow-sm hover:opacity-90 transition"
                  style={{ 
                    backgroundColor: 'var(--lk-sage, #7E8F73)', 
                    color: 'white',
                    border: '1px solid var(--lk-sage-dark, #5C6B52)'
                  }}
                  data-testid="my-world-nav"
                >
                  <User className="w-4 h-4" />
                  Base Camp
                </button>
              )}
              <div className="w-8 h-8 rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary, #F0F0F0)' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                  alt="LikeKind" 
                  className="w-8 h-8 object-cover"
                />
              </div>
              <span className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                Explore Activities
              </span>
            </div>

            <div className="flex items-center gap-2">
              {user ? (
                <button
                  onClick={() => navigate('/me')}
                  className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                  style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}
                  aria-label="My profile"
                >
                  <User className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={() => navigate('/auth')}
                  className="px-4 py-2 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 transition"
                  style={{ 
                    backgroundColor: 'var(--lk-sage, #7E8F73)', 
                    color: 'white',
                    border: '1px solid var(--lk-sage-dark, #5C6B52)'
                  }}
                  data-testid="explore-sign-in-btn"
                >
                  Sign in
                </button>
              )}
              <button
                onClick={() => navigate('/settings')}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}
                aria-label="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Categories */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="animate-pulse p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-background-secondary, #F5F5F5)' }}>
                <div className="h-8 w-8 rounded mb-2" style={{ backgroundColor: 'var(--fy-border-subtle, #E5E5E5)' }} />
                <div className="h-5 w-24 rounded" style={{ backgroundColor: 'var(--fy-border-subtle, #E5E5E5)' }} />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Circles - First-class main category, centered above all others */}
            <div className="flex justify-center mb-3">
              <button
                onClick={() => navigate('/circles')}
                className="overflow-hidden transition hover:shadow-sm flex flex-col justify-center items-center"
                style={{ 
                  width: 'calc(50% - 6px)',
                  aspectRatio: '1 / 1',
                  backgroundColor: '#FAFAF8',
                  border: '1px solid #E5E5E0',
                  borderRadius: '16px',
                }}
              >
                <img
                  src={CIRCLES_TILE}
                  alt=""
                  draggable={false}
                  className="select-none pointer-events-none"
                  style={{
                    maxWidth: '72%',
                    maxHeight: '72%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                <div 
                  className="flex items-center justify-between w-full px-4"
                  style={{ marginTop: '12px' }}
                >
                  <span 
                    className="text-base font-medium"
                    style={{ color: '#1A1A1A' }}
                  >
                    Circles
                  </span>
                  <span style={{ color: '#999' }} aria-hidden="true">→</span>
                </div>
              </button>
            </div>

            {/* Category Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-3">
              {categories.map((category) => {
                const subs = subcategories[category.id] || [];
                // Priority: 1) Brand illustration, 2) API image, 3) Fallback
                const brandIllustration = CATEGORY_ILLUSTRATIONS[category.id];
                const tileImage = brandIllustration || category.image || TILE_IMAGES_FALLBACK[category.name];
                const hasBrandIllustration = !!brandIllustration;
                
                return (
                  <div key={category.id}>
                    {/* Category Tile - Per spec: aspect-ratio 1/1, flexbox centered */}
                    <button
                      onClick={() => handleCategoryClick(category.id)}
                      className={`
                        w-full overflow-hidden transition
                        hover:shadow-sm
                        flex flex-col justify-center items-center
                        ${selectedCategory === category.id ? 'ring-2 ring-green-500' : ''}
                      `}
                      style={{ 
                        aspectRatio: '1 / 1',
                        backgroundColor: '#FAFAF8',
                        border: '1px solid #E5E5E0',
                        borderRadius: '16px',
                      }}
                      data-testid={`category-${category.id}`}
                    >
                      {/* Image - Brand illustration or fallback */}
                      {tileImage ? (
                        <img
                          src={tileImage}
                          alt=""
                          draggable={false}
                          className="select-none pointer-events-none"
                          style={{
                            maxWidth: hasBrandIllustration ? '80%' : '72%',
                            maxHeight: hasBrandIllustration ? '80%' : '72%',
                            objectFit: hasBrandIllustration ? 'contain' : 'cover',
                            borderRadius: hasBrandIllustration ? '0' : '12px',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <span className="text-4xl">{category.icon}</span>
                      )}
                    
                    {/* Label */}
                    <div 
                      className="flex items-center justify-between w-full px-4"
                      style={{ marginTop: '12px' }}
                    >
                      <span 
                        className="text-base font-medium"
                        style={{ color: '#1A1A1A' }}
                      >
                        {category.icon} {category.name}
                      </span>
                      <span style={{ color: '#999' }} aria-hidden="true">→</span>
                    </div>
                  </button>

                  {/* Subcategories - Expanded */}
                  {selectedCategory === category.id && (
                    <div 
                      className="mt-2 p-3 space-y-1"
                      style={{ 
                        backgroundColor: '#F5F5F0',
                        borderRadius: '16px',
                      }}
                    >
                      {subs.length > 0 ? subs.map((sub) => {
                        const isFollowing = followedSubcategories.includes(sub.id);
                        return (
                          <div
                            key={sub.id}
                            className="w-full flex items-center gap-2 p-3 rounded-lg transition-colors hover:bg-white"
                          >
                            <button
                              onClick={() => handleSubcategoryClick(sub.id, sub.slug)}
                              className="flex-1 text-left"
                            >
                              <span 
                                className="font-medium text-sm"
                                style={{ color: '#1A1A1A' }}
                              >
                                {sub.name}
                              </span>
                            </button>
                            {user && (
                              <button
                                onClick={(e) => toggleFollowSubcategory(sub.id, e)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                  isFollowing ? '' : 'border'
                                }`}
                                style={{ 
                                  backgroundColor: isFollowing ? 'var(--lk-sage, #7E8F73)' : 'transparent',
                                  color: isFollowing ? 'white' : '#666',
                                  borderColor: '#DDD'
                                }}
                                data-testid={`follow-${sub.id}`}
                              >
                                {isFollowing ? (
                                  <span className="flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Following
                                  </span>
                                ) : 'Follow'}
                              </button>
                            )}
                            <button
                              onClick={() => handleSubcategoryClick(sub.id, sub.slug)}
                              className="p-1"
                            >
                              <ChevronRight className="w-4 h-4" style={{ color: '#999' }} />
                            </button>
                          </div>
                        );
                      }) : (
                        <div className="p-3 text-sm text-center" style={{ color: '#999' }}>
                          Loading...
                        </div>
                      )}
                      
                      {/* Create action - only for logged in users */}
                      {user && subs.length > 0 && (
                        <button
                          onClick={() => navigate(`/create-server?category=${category.id}`)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors mt-2 shadow-sm hover:opacity-90"
                          style={{ 
                            backgroundColor: 'var(--lk-sage, #7E8F73)', 
                            color: 'white',
                            border: '1px solid var(--lk-sage-dark, #5C6B52)'
                          }}
                          data-testid={`create-in-${category.id}`}
                        >
                          <Plus className="w-5 h-5" />
                          <span className="font-semibold text-sm">Create activity in {category.name}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* SEED CONTENT: Show for browse-for-free (unauthenticated) users */}
          {!user && (seedGroups.length > 0 || seedCommons.length > 0) && (
            <div className="mt-8 space-y-6">
              {/* Example Groups Section */}
              {seedGroups.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                      Example Groups
                    </h3>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--lk-sage-light, #E8EDE5)', color: 'var(--lk-sage-dark, #5C6B52)' }}>
                      Sample content
                    </span>
                  </div>
                  <div className="space-y-3">
                    {seedGroups.slice(0, 4).map((group) => (
                      <button
                        key={group.group_id}
                        onClick={() => navigate('/auth')}
                        className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors hover:shadow-sm text-left"
                        style={{ 
                          backgroundColor: '#FAFAF8',
                          border: '1px solid #E5E5E0'
                        }}
                      >
                        <img 
                          src={group.image} 
                          alt={group.name}
                          className="w-14 h-14 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate" style={{ color: '#1A1A1A' }}>
                            {group.name}
                          </h4>
                          <p className="text-xs truncate mt-0.5" style={{ color: '#666' }}>
                            {group.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs" style={{ color: '#999' }}>
                              {group.member_count} members
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F0F0EB', color: '#666' }}>
                              {group.category}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#999' }} />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-center mt-3" style={{ color: '#999' }}>
                    Create an account to join groups in your area
                  </p>
                </div>
              )}
              
              {/* Example Commons Section */}
              {seedCommons.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                      Example Commons
                    </h3>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--lk-sage-light, #E8EDE5)', color: 'var(--lk-sage-dark, #5C6B52)' }}>
                      Sample content
                    </span>
                  </div>
                  <div className="space-y-3">
                    {seedCommons.map((commons) => (
                      <button
                        key={commons.commons_id}
                        onClick={() => navigate('/auth')}
                        className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors hover:shadow-sm text-left"
                        style={{ 
                          backgroundColor: '#FAFAF8',
                          border: '1px solid #E5E5E0'
                        }}
                      >
                        <img 
                          src={commons.image} 
                          alt={commons.name}
                          className="w-14 h-14 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate" style={{ color: '#1A1A1A' }}>
                            {commons.name}
                          </h4>
                          <p className="text-xs truncate mt-0.5" style={{ color: '#666' }}>
                            {commons.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs" style={{ color: '#999' }}>
                              {commons.member_count} members
                            </span>
                            <span className="text-xs" style={{ color: '#999' }}>
                              {commons.group_count} groups
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#999' }} />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-center mt-3" style={{ color: '#999' }}>
                    Create an account to discover Commons in your area
                  </p>
                </div>
              )}
            </div>
          )}
          </>
        )}

        <div className="h-20" />
      </main>

      {/* Orientation Overlay */}
      {showOrientation && (
        <OrientationCarousel 
          onComplete={handleOrientationComplete}
          onSkip={handleOrientationComplete}
        />
      )}
    </div>
  );
}
